/** Bottom band of a split portrait recording. The trade screen fills the rest. */
export const WEBCAM_BAND_FRACTION = 1 / 3;

export const VIDEO_FORMATS = {
  portrait: {
    key: 'portrait',
    label: 'Portrait (9:16)',
    previewWidth: 405,
    previewHeight: 720,
    outputWidth: 1080,
    outputHeight: 1920,
    aspectRatio: '9/16',
    layout: 'overlay',
    defaultScreen: 'mobile',
    showScreenPicker: true,
    defaultWebcamSize: 240,
    description:
      'Portrait 1080×1920. The whole Trade tab is kept, without cropping. On Edit, drag and zoom it inside the frame, then download the screen, the webcam, or the combined video.',
  },
  landscape: {
    key: 'landscape',
    label: 'Landscape (16:9)',
    previewWidth: 640,
    previewHeight: 360,
    outputWidth: 1920,
    outputHeight: 1080,
    aspectRatio: '16/9',
    layout: 'overlay',
    defaultScreen: 'desktop',
    showScreenPicker: false,
    defaultWebcamSize: 120,
    description:
      'Landscape 1920×1080. The whole Trade tab is kept. On Edit, drag and zoom it, then download the screen, the webcam, or the combined video.',
  },
  split: {
    key: 'split',
    label: 'Split portrait',
    previewWidth: 405,
    previewHeight: 720,
    outputWidth: 1080,
    outputHeight: 1920,
    aspectRatio: '9/16',
    layout: 'webcam-bottom',
    defaultScreen: 'desktop',
    showScreenPicker: true,
    defaultWebcamSize: 240,
    description:
      'Portrait 1080×1920. Top two-thirds is the Trade tab, bottom third is your webcam. The full tab is recorded. On Edit, drag and zoom the screen inside that top area.',
  },
};

/**
 * @param {string} [search] location.search. Omit to read the current window.
 * @returns {'mobile' | 'desktop' | null}
 */
export function videoCaptureUiMode(search) {
  const raw = search == null
    ? (typeof window !== 'undefined' ? window.location.search : '')
    : String(search);
  const query = raw.includes('?') ? raw.slice(raw.indexOf('?') + 1) : raw;
  const mode = new URLSearchParams(query).get('videoCapture');
  if (mode === '1' || mode === 'mobile') return 'mobile';
  if (mode === 'desktop') return 'desktop';
  return null;
}

export function tradePathForScreen(screen) {
  return screen === 'desktop' ? '/trade?videoCapture=desktop' : '/trade?videoCapture=1';
}

const CAPTURE_ROOTS = ['/trade', '/dashboard', '/onboarding'];

/**
 * Path loaded inside the phone capture window.
 * Rejects anything that is not an in-app route, and keeps the phone layout flag.
 */
export function captureIframeSrc(rawPath) {
  const fallback = '/trade?videoCapture=1';
  if (!rawPath || typeof rawPath !== 'string') return fallback;
  const trimmed = rawPath.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.includes('://') || trimmed.includes('..')) {
    return fallback;
  }
  let url;
  try {
    url = new URL(trimmed, 'http://precisiontrader.local');
  } catch (_) {
    return fallback;
  }
  const ok = CAPTURE_ROOTS.some((root) => url.pathname === root || url.pathname.startsWith(`${root}/`));
  if (!ok) return fallback;
  if (!url.searchParams.get('videoCapture')) url.searchParams.set('videoCapture', '1');
  return `${url.pathname}${url.search}`;
}

/**
 * Screen region and optional full-width webcam band, in output pixels.
 * Split layout: webcam is the bottom third; the trade screen is everything above it.
 */
export function frameRegions(outputWidth, outputHeight, layout, webcamEnabled) {
  if (layout === 'webcam-bottom' && webcamEnabled) {
    const webcamH = Math.round(outputHeight * WEBCAM_BAND_FRACTION);
    const screenH = outputHeight - webcamH;
    return {
      screen: { x: 0, y: 0, width: outputWidth, height: screenH },
      webcam: { x: 0, y: screenH, width: outputWidth, height: webcamH },
    };
  }
  return {
    screen: { x: 0, y: 0, width: outputWidth, height: outputHeight },
    webcam: null,
  };
}

/** CSS layout size of the Trade page while recording a phone. Wider than this hits tablet breakpoints. */
export const PHONE_WINDOW = { width: 390, height: 844 };

/**
 * Largest phone-shaped window that fits the screen.
 * The Trade page itself stays at PHONE_WINDOW and is zoomed to fill this,
 * so the layout stays a phone and the capture uses the screen's pixels.
 */
export function phoneCaptureWindow({ availWidth, availHeight } = {}) {
  const maxW = Math.max(PHONE_WINDOW.width, (availWidth || PHONE_WINDOW.width) - 8);
  const maxH = Math.max(PHONE_WINDOW.height, (availHeight || PHONE_WINDOW.height) - 8);
  const scale = Math.max(1, Math.min(maxW / PHONE_WINDOW.width, maxH / PHONE_WINDOW.height));
  return {
    width: Math.round(PHONE_WINDOW.width * scale),
    height: Math.round(PHONE_WINDOW.height * scale),
    scale,
  };
}

/**
 * Pixel size of the Trade window to open.
 * Mobile opens as large as the screen allows, in phone aspect.
 * Desktop opens larger than the frame so the recording scales down.
 */
export function captureWindowSize(format, screen, bounds) {
  if (screen === 'mobile') {
    const phone = phoneCaptureWindow(bounds);
    return { width: phone.width, height: phone.height };
  }
  if (format.layout === 'webcam-bottom') {
    return { width: 1600, height: 1800 };
  }
  if (format.outputHeight > format.outputWidth) {
    return { width: 1440, height: 2560 };
  }
  return { width: 2560, height: 1440 };
}

/** Source rectangle used by object-fit: cover, in source pixels. */
export function coverCropSize(srcW, srcH, destAspect) {
  if (!srcW || !srcH || !destAspect) return { sw: 0, sh: 0 };
  const srcAspect = srcW / srcH;
  if (srcAspect > destAspect) {
    const sh = srcH;
    return { sw: sh * destAspect, sh };
  }
  const sw = srcW;
  return { sw, sh: sw / destAspect };
}

/** True when a cover-fit into dest does not have to enlarge the capture. */
export function captureIsSharp(srcW, srcH, destW, destH) {
  if (!destW || !destH) return false;
  const { sw, sh } = coverCropSize(srcW, srcH, destW / destH);
  return sw + 0.5 >= destW && sh + 0.5 >= destH;
}

/** scale 1 fits the entire source in the region. x/y pan as a fraction of the region, 0 = centered. */
export const DEFAULT_SCREEN_PLACEMENT = { scale: 1, x: 0, y: 0 };

/**
 * Destination rect for the full source image inside a region.
 * Scale 1 is object-fit: contain, so a wide desktop stays fully visible.
 */
export function screenDrawRect(srcW, srcH, region, placement = DEFAULT_SCREEN_PLACEMENT) {
  if (!srcW || !srcH || !region?.width || !region?.height) {
    return { dx: region?.x || 0, dy: region?.y || 0, dw: 0, dh: 0 };
  }
  const scale = placement.scale > 0 ? placement.scale : 1;
  const contain = Math.min(region.width / srcW, region.height / srcH);
  const dw = srcW * contain * scale;
  const dh = srcH * contain * scale;
  const dx = region.x + (region.width - dw) / 2 + (placement.x || 0) * region.width;
  const dy = region.y + (region.height - dh) / 2 + (placement.y || 0) * region.height;
  return { dx, dy, dw, dh };
}

/** Keep scale in range and keep at least a sliver of the screen inside the region. */
export function clampScreenPlacement(placement, srcW, srcH, region) {
  const scale = Math.min(8, Math.max(0.25, placement?.scale > 0 ? placement.scale : 1));
  const unclamped = screenDrawRect(srcW, srcH, region, {
    scale,
    x: placement?.x || 0,
    y: placement?.y || 0,
  });
  const minOverlap = Math.min(80, region.width, region.height);
  let left = unclamped.dx - region.x;
  let top = unclamped.dy - region.y;
  left = Math.min(region.width - minOverlap, Math.max(minOverlap - unclamped.dw, left));
  top = Math.min(region.height - minOverlap, Math.max(minOverlap - unclamped.dh, top));
  const centeredLeft = (region.width - unclamped.dw) / 2;
  const centeredTop = (region.height - unclamped.dh) / 2;
  return {
    scale,
    x: (left - centeredLeft) / region.width,
    y: (top - centeredTop) / region.height,
  };
}

/** Screen-content bitrate. Floor stays above the previous 18 Mbps encode. */
export function recordingVideoBitrate(width, height) {
  const bps = Math.round(width * height * 30 * 0.3);
  return Math.min(40_000_000, Math.max(20_000_000, bps));
}
